// The Customers table's inferred columns. Every value is read from data the
// Customer Graph already stores on `contact_projections`, so showing them
// costs no extra AI calls:
//   Interested in  -> AI summary `predicted_next`, else favourite services
//   Best time      -> computed `active_hours` (when they message and book)
//   Sentiment      -> AI summary `sentiment`
//   Next best step -> AI summary `reengagement_angle`

export type CustomerSentiment = "positive" | "neutral" | "frustrated";

// Badge colours follow STAGE_META in pipelineStage.ts.
export const SENTIMENT_META: Record<CustomerSentiment, { label: string; badge: string }> = {
  positive: { label: "Positive", badge: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30" },
  neutral: { label: "Neutral", badge: "bg-orange-500/10 text-orange-700 border-orange-500/30" },
  frustrated: { label: "Frustrated", badge: "bg-rose-500/15 text-rose-700 border-rose-500/30" },
};

export interface CustomerInsights {
  interest: string | null;
  bestTime: string | null;
  sentiment: CustomerSentiment | null;
  nextStep: string | null;
}

export const EMPTY_CUSTOMER_INSIGHTS: CustomerInsights = {
  interest: null,
  bestTime: null,
  sentiment: null,
  nextStep: null,
};

// Mirrors CURRENT_SYNOPSIS_SCHEMA_VERSION and the 14-day expiry the hourly
// refresh enforces (supabase/functions/_shared/customer_graph/).
const MIN_SUMMARY_SCHEMA_VERSION = 3;
const MAX_SUMMARY_AGE_MS = 14 * 86_400_000;

export interface CustomerInsightProjection {
  active_hours?: unknown;
  top_listings?: unknown;
  synopsis_llm_generated_at?: string | null;
  synopsis_schema_version?: number | null;
  predicted_next?: string | null;
  reengagement_angle?: string | null;
  sentiment?: string | null;
}

const text = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : null;

export function formatBestTime(activeHours: unknown): string | null {
  if (!activeHours || typeof activeHours !== "object") return null;
  const { band, weekend_lean: lean } = activeHours as { band?: unknown; weekend_lean?: unknown };
  if (!["morning", "afternoon", "evening", "night"].includes(String(band))) return null;
  const plural = `${band}s`;
  if (lean === "weekday") return `Weekday ${plural}`;
  if (lean === "weekend") return `Weekend ${plural}`;
  return plural.charAt(0).toUpperCase() + plural.slice(1);
}

function favouriteServices(topListings: unknown): string | null {
  if (!Array.isArray(topListings)) return null;
  const names = topListings
    .map((listing) => text((listing as { name?: unknown } | null)?.name))
    .filter((name): name is string => !!name)
    .slice(0, 2);
  return names.length > 0 ? names.join(", ") : null;
}

/**
 * Inferred values are shown while the summary is on the current schema and
 * inside the backend's own 14-day expiry. Unlike the profile brief, a newer
 * message does not blank them: the refresh is throttled to once a day, and an
 * empty column for the most active customers would read as "nothing known".
 */
export function customerInsightsFromProjection(
  projection: CustomerInsightProjection | null | undefined,
  now = Date.now(),
): CustomerInsights {
  if (!projection) return EMPTY_CUSTOMER_INSIGHTS;
  const generatedMs = projection.synopsis_llm_generated_at
    ? new Date(projection.synopsis_llm_generated_at).getTime()
    : Number.NaN;
  const summaryUsable = Number(projection.synopsis_schema_version ?? 0) >= MIN_SUMMARY_SCHEMA_VERSION &&
    Number.isFinite(generatedMs) &&
    now - generatedMs <= MAX_SUMMARY_AGE_MS;
  const sentiment = summaryUsable ? projection.sentiment : null;

  return {
    interest: (summaryUsable ? text(projection.predicted_next) : null) ?? favouriteServices(projection.top_listings),
    bestTime: formatBestTime(projection.active_hours),
    sentiment: sentiment === "positive" || sentiment === "neutral" || sentiment === "frustrated" ? sentiment : null,
    nextStep: summaryUsable ? text(projection.reengagement_angle) : null,
  };
}
